import { Program } from '@playbook-technologies/common-enums';
import { Collection } from './collection.domain';
import { isEmpty } from 'lodash';
import { Price } from './domain';
import { z } from 'zod';

/**
 * Checks hard requirements for a PUBLISHED Program at save time.
 * Returns an array of human-readable error messages (empty = no violations).
 *
 * Only applies to Program types (`type === Program.Type.Program.value`).
 * Category types are explicitly excluded — their hard-req set differs and
 * the guard is only intended for Programs per the business-logic spec.
 *
 * Accepts both the domain `Collection` shape and raw GraphQL response data
 * (the predicate is called twice per save: once on the candidate payload,
 * once on the server-persisted copy for the diff-aware check).
 *
 * This predicate intentionally does NOT check `duration` — that field is
 * overridden post-validation in the save path for category types, and we
 * run validation on the pre-override object.
 */
export const checkPublishedProgramHardReqs = (collection: {
  type?: number | null;
  name?: string | null;
  mediaCover?: { cdn01?: string | null; image?: string | null } | null;
  goal?: string | null;
  workouts?: Array<unknown> | null;
}): Array<string> => {
  if (collection.type !== Program.Type.Program.value) return [];

  const errors: Array<string> = [];

  if (!collection.name?.trim()) {
    errors.push('About Tab: Title is required.');
  }

  if (
    !collection.mediaCover?.cdn01?.trim() ||
    !collection.mediaCover?.image?.trim()
  ) {
    errors.push('About Tab: Cover is required.');
  }

  if (!collection.goal?.trim()) {
    errors.push('About Tab: Goal is required.');
  }

  if (!collection.workouts || collection.workouts.length === 0) {
    errors.push('Workouts Tab: At least one workout must be published.');
  }

  return errors;
};

const coverError = 'About Tab: Cover is required.';
const programLengthError = 'About Tab: Program Length is required.';
const workoutSchema = z.object({
  workoutId: z.number(),
  name: z.string(),
});
export const readyBaseCollectionSchema = z.object({
  id: z.number(),
  name: z.string().min(1, 'About Tab: Title is required.'),
  mediaCover: z
    .object({
      cdn01: z
        .string({ required_error: coverError })
        .min(1, { message: coverError })
        .nullable()
        .refine((value) => value !== null, { message: coverError }),
      image: z
        .string({ required_error: coverError })
        .min(1, { message: coverError })
        .nullable()
        .refine((value) => value !== null, { message: coverError }),
    })
    .nullable()
    .optional()
    .refine((value) => !isEmpty(value), { message: coverError }),
  color: z.string().regex(/^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, {
    message: 'About Tab: Color must be a valid hex color code.',
  }),
  workouts: z.array(workoutSchema).min(1, {
    message: 'Workouts Tab: At least one workout must be published.',
  }),
});
export const readyProgramSchema = readyBaseCollectionSchema.extend({
  goal: z.string().min(1, 'About Tab: Goal is required.'),
  duration: z
    .tuple([
      z
        .string()
        .regex(/^\d+$/, { message: programLengthError })
        .refine(
          (value) => parseInt(value, 10) >= 1 && parseInt(value, 10) <= 700,
          {
            message: programLengthError,
          },
        ),
      z.string().min(1, { message: programLengthError }),
    ])
    .nullable()
    .refine((value) => value !== null, { message: programLengthError })
    .optional(),
});

export type ReadyCategory = z.infer<typeof readyBaseCollectionSchema>;
export type ReadyProgram = z.infer<typeof readyProgramSchema>;

export const validateToPublish = async (
  collection: Collection,
  price: Price,
) => {
  if (collection.type == Program.Type.Program.value) {
    await readyProgramSchema.parseAsync(collection);
  } else {
    await readyBaseCollectionSchema.parseAsync(collection);
  }
  const mappedErrors = [];
  if (isEmpty(collection.workouts)) {
    mappedErrors.push('Workouts are missing. Add at least one workout');
  }
  if (
    collection.accessType === Program.AccessType.Exclusive.value &&
    !price.price
  ) {
    mappedErrors.push('Price is missing');
  }
  return mappedErrors;
};
